import type { WizardState } from '../lib/wizardSteps.ts';
import type { Client } from '../lib/types.ts';
import { sectionDesc } from '../lib/ui.ts';
import {
  GOLIVE_ITEMS,
  goliveProgress,
  itemSatisfied,
  type GoLiveItem,
  type GoLiveMarks,
  type GoLiveRollups,
  type GoLiveState,
} from '../lib/goliveChecklist.ts';

/**
 * Go-live checklist (go-live-checklist.md §B/§C) — the per-client human-VERIFY launch list,
 * built into the flow so nothing is a forgotten task. Mirrors ResponsiveChecklist: tri-state per
 * item, persisted in wizard_state.golive, a progress badge. Shown at Preview & approve and again
 * at Go live. Each item is ☐ open · ✅ done · ⊘ N/A (satisfied = done OR N/A). Items 1 (responsive)
 * and 9 (client approved) are READ-ONLY roll-ups of existing state — reflected, never duplicated.
 *
 * The Go-live action soft-gates on the progress (a confirm when items are open) — that lives in the
 * Wizard's goLive(), using goliveProgress(); this component is the surface + the manual ticks.
 */
export function GoLiveChecklist({
  client,
  ws,
  onPersist,
  canMaintainLive = false,
  rollups,
}: {
  client: Client;
  ws: WizardState;
  onPersist: (next: WizardState) => void;
  canMaintainLive?: boolean;
  rollups: GoLiveRollups;
}) {
  const marks: GoLiveMarks = ws.golive ?? {};
  // Same contract as ResponsiveChecklist: a live/protected client is read-only here unless the
  // admin maintenance carve-out applies (wizard_state is DB-only metadata; the save is already
  // is_live-gated with the #GR89 admin carve-out — non-admins / protected slugs 403 server-side).
  const liveBlocked = client.is_live && !canMaintainLive;
  const progress = goliveProgress(marks, rollups);

  // Toggle a MANUAL item to done/na; clicking the active state again clears it back to open
  // (open = key absent, so the stored shape stays Record<id,'done'|'na'>).
  function setMark(id: string, next: GoLiveState) {
    if (liveBlocked) return;
    const nextMarks: GoLiveMarks = { ...marks };
    if (nextMarks[id] === next) delete nextMarks[id];
    else nextMarks[id] = next;
    onPersist({ ...ws, golive: nextMarks });
  }

  const groups: { title: string; phase: GoLiveItem['phase'] }[] = [
    { title: 'Final tests', phase: 'final' },
    { title: 'Go-live launch', phase: 'launch' },
  ];

  return (
    <div className="card p-5 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-medium text-ink">Go-live checklist</p>
          <p className={sectionDesc}>
            The per-client launch checks. Nothing is forced — but Go live will flag anything still open.
          </p>
        </div>
        <span className="rounded bg-paper-2 px-2 py-0.5 text-xs text-ink-2">
          {progress.satisfied}/{progress.total} done
        </span>
      </div>

      {liveBlocked && (
        <p className="mt-2 rounded border border-warning-bright bg-warning-bright/10 p-2 text-xs text-warning">
          This client is live/protected — the checklist is read-only here.
        </p>
      )}

      <div className="mt-3 space-y-3">
        {groups.map((g) => {
          const rows = GOLIVE_ITEMS.filter((it) => it.phase === g.phase);
          return (
            <div key={g.phase}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{g.title}</p>
              <ul className="mt-1 space-y-1">
                {rows.map((it) => {
                  const satisfied = itemSatisfied(it, marks, rollups);
                  return (
                    <li
                      key={it.id}
                      className="flex flex-wrap items-center gap-2 rounded border border-line p-2"
                    >
                      <span className={`flex h-4 w-4 flex-none items-center justify-center text-xs ${satisfied ? 'text-success' : 'text-ink-2'}`}>
                        {satisfied ? '✓' : '○'}
                      </span>
                      <span className="flex-1 text-ink">
                        {it.label}
                        {it.naHint && <span className="text-[11px] text-ink-2"> — {it.naHint}</span>}
                      </span>
                      {it.derived ? (
                        // Read-only roll-up of existing state (items 1 + 9).
                        <span className="rounded bg-paper-2 px-2 py-0.5 text-[11px] text-ink-2">
                          {satisfied
                            ? it.derived === 'responsive'
                              ? 'from the responsive check ✓'
                              : 'client approved ✓'
                            : it.derived === 'responsive'
                              ? 'check all viewports'
                              : 'not approved yet'}
                        </span>
                      ) : (
                        <span className="flex flex-none gap-1">
                          <button
                            type="button"
                            onClick={() => setMark(it.id, 'done')}
                            disabled={liveBlocked}
                            className={`rounded border px-2 py-0.5 text-[11px] ${
                              marks[it.id] === 'done'
                                ? 'border-success bg-success-bright/10 text-success'
                                : 'border-line text-ink-2 hover:bg-paper-2'
                            } disabled:opacity-40`}
                          >
                            Done
                          </button>
                          <button
                            type="button"
                            onClick={() => setMark(it.id, 'na')}
                            disabled={liveBlocked}
                            className={`rounded border px-2 py-0.5 text-[11px] ${
                              marks[it.id] === 'na'
                                ? 'border-ink-2 bg-paper-2 text-ink'
                                : 'border-line text-ink-2 hover:bg-paper-2'
                            } disabled:opacity-40`}
                            title="Not applicable to this client"
                          >
                            N/A
                          </button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
